import { AppConfig } from '@/constants/AppConfig';
import { UserApi } from '@/utilities/api/UserApi';
import { getUserToken } from '@/utilities/Cookies';
import { getUserDisplayName } from '@/utilities/Farmer';
import { UserEndpoint } from '@api/endpoints';
import { ApiUser } from '@api/interfaces';
import { UserType } from '@shared/enums';
import { isRetailerUser } from '@shared/utilities';

/** How the switcher gets back into an account: LP's own token, or a Cognito session's ticket. */
type Credential = UserEndpoint.SwitchAccount.Request;

type AccountList = {
  [id: string]: Credential & {
    name: string,
    type: UserType,
  },
}

type Account = {
  id: string,
  name: string,
  type: UserType,
}

const key = "Bidding_Accounts";

function read () {
  return JSON.parse(localStorage.getItem(key) ?? '{}') as AccountList;
}

function write (list: AccountList) {
  localStorage.setItem(key, JSON.stringify(list));
}

/**
 * Used in concert with UserSwitcher to keep each account's session in Local Storage.
 */
export class LocalAccountList {
  static clear () {
    write({});
  }

  static get (id: string) {
    return read()[id];
  }

  static canHold (user: ApiUser): boolean {
    return !AppConfig.env.prod && (user.userType === UserType.Internal || this.isActive());
  }

  static isActive (): boolean {
    if (AppConfig.env.prod) { return false; }
    const accounts = Object.values(read());
    return accounts.some((account) => account.type === UserType.Internal);
  }

  static list ()  {
    const list = read();
    const accounts: Account[] = [];
    Object.keys(list).forEach((id) => {
      accounts.push({
        id,
        name: list[id].name,
        type: list[id].type,
      });
    });
    return accounts;
  }

  static remove (id: string) {
    const list = read();
    delete list[id];
    write(list);
  }

  /** Keeps the signed-in account's session, of whichever kind the sign-in produced. */
  static async remember (user: ApiUser) {
    if (!this.canHold(user)) {
      return;
    }
    const token = getUserToken();
    if (token) {
      this.set(user, { token });
      return;
    }
    // A missing ticket only leaves this account out of the switcher; the sign-in still stands.
    const ticket = await UserApi.getSwitchAccountTicket()
      .then((response) => response.ticket)
      .catch(() => undefined);
    if (ticket) {
      this.set(user, { ticket });
    }
  }

  static set (user: ApiUser, credential: Credential) {
    if (!this.canHold(user)) {
      return;
    }
    const userName = getUserDisplayName(user);
    const name = isRetailerUser(user)
      ? user.businessName!
      : userName;

    const list = read();
    list[user.id] = {
      ...credential,
      name,
      type: user.userType,
    };
    write(list);
  }
}
